/** How long to keep the frame alive after printing, so a slow dialog still has its document. */
const FRAME_LIFETIME_MS = 60_000;

/**
 * Prints a PDF by URL, showing the browser's print dialog directly.
 *
 * The file is fetched and re-served as a blob so the frame holding it is same-origin: a frame
 * pointed straight at the presigned URL is cross-origin, and its `print()` is unreachable.
 *
 * Rejects rather than degrading quietly, so the caller can fall back to opening a tab. Fetching
 * depends on the bucket allowing this origin, and printing a PDF from a frame is well supported in
 * Chrome and Edge but not everywhere.
 */
export async function printPdfInFrame(url: string): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Could not fetch the PDF: ${response.status}`);

  const blobUrl = URL.createObjectURL(await response.blob());
  const frame = document.createElement('iframe');
  // Off-screen rather than hidden: Chrome routes print() to the parent document when the frame has
  // no size, which prints the application around the report instead of the report.
  frame.style.position = 'fixed';
  frame.style.left = '-9999px';
  frame.style.top = '0';
  frame.style.width = '210mm';
  frame.style.height = '297mm';
  frame.style.border = '0';
  frame.title = 'Print frame';

  const cleanUp = () => {
    frame.remove();
    URL.revokeObjectURL(blobUrl);
  };

  try {
    await new Promise<void>((resolve, reject) => {
      frame.onload = () => resolve();
      frame.onerror = () => reject(new Error('The print frame could not load the PDF'));
      frame.src = blobUrl;
      document.body.appendChild(frame);
    });

    const view = frame.contentWindow;
    if (!view) throw new Error('The print frame has no window');

    view.focus();
    view.print();
  } catch (error: unknown) {
    cleanUp();
    throw error;
  }

  // Left in place while the dialog is open: removing the frame takes the document with it, and the
  // dialog is not modal to this script.
  window.setTimeout(cleanUp, FRAME_LIFETIME_MS);
}
